import React from "react";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import ExploreRoundedIcon from "@mui/icons-material/ExploreRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import { useTranslation } from "react-i18next";
import type { TranslationKey } from "../../lib/i18n";

export type NavTab =
  | "landing"
  | "dashboard"
  | "calls"
  | "voice-agent"
  | "pipeline"
  | "quota"
  | "marketplace"
  | "services"
  | "onboarding"
  | "admin"
  | "settings"
  | "profile"
  | "portfolio"
  | "calendar"
  | "statistics"
  | "subscription";

interface SidebarProps {
  currentTab: NavTab;
  onSelectTab: (tab: NavTab) => void;
  colorMode: "light" | "dark";
  onToggleColorMode: () => void;
  /** Onglets affichés ; par défaut tous (espace pro/admin). */
  visibleTabs?: string[];
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentTab,
  onSelectTab,
  colorMode,
  onToggleColorMode,
  visibleTabs,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isLight = colorMode === "light";

  const navItems: { id: NavTab; icon: React.ReactNode; labelKey: TranslationKey }[] = [
    { id: "landing", icon: <HomeRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.landing" },
    { id: "dashboard", icon: <DashboardRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.dashboard" },
    { id: "marketplace", icon: <ExploreRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.search" },
    { id: "profile", icon: <PersonOutlineRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.profile" },
    { id: "calls", icon: <ReceiptLongRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.calls" },
    { id: "voice-agent", icon: <RecordVoiceOverRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.voiceAgent" },
    { id: "pipeline", icon: <PeopleAltRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.pipeline" },
    { id: "services", icon: <StorefrontRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.services" },
    { id: "quota", icon: <SpeedRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.quota" },
    { id: "statistics", icon: <InsightsRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.statistics" },
    { id: "subscription", icon: <WorkspacePremiumRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.subscription" },
    { id: "portfolio", icon: <PhotoLibraryRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.portfolio" },
    { id: "calendar", icon: <EventNoteRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.calendar" },
    { id: "admin", icon: <AdminPanelSettingsRoundedIcon sx={{ fontSize: 20 }} />, labelKey: "nav.admin" },
  ];

  return (
    <Box
      component="nav"
      aria-label="Navigation principale"
      sx={{
        width: 72,
        height: "100vh",
        position: "sticky",
        top: 0,
        flexShrink: 0,
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        py: 2.5,
        bgcolor: isLight ? "#FFFFFF" : "#141417",
        borderRight: "1px solid",
        borderColor: "divider",
        zIndex: 10,
        overflowY: "auto",
        transition: "border-color 0.2s ease, background-color 0.2s ease",
      }}
    >
      {/* Top Navigation Items */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "center",
          maxHeight: "100%",
          overflowX: "hidden",
        }}
      >
        {navItems
          .filter((item) => visibleTabs?.includes(item.id) ?? item.id !== "landing")
          .map((item) => {
          const isActive = currentTab === item.id;
          return (
            <Tooltip key={item.id} title={t(item.labelKey)} placement="right" arrow>
              <IconButton
                onClick={() => onSelectTab(item.id)}
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  color: isActive ? "#FFFFFF" : "text.secondary",
                  bgcolor: isActive ? "#FF3567" : "transparent",
                  boxShadow: isActive
                    ? "0 4px 14px rgba(255, 53, 103, 0.35)"
                    : "none",
                  transition: "all 0.15s ease-in-out",
                  "&:hover": {
                    bgcolor: isActive ? "#E62053" : "action.hover",
                    color: isActive ? "#FFFFFF" : "text.primary",
                  },
                }}
              >
                {item.icon}
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>

      {/* Bottom Controls: Theme Toggle & Settings */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "center",
        }}
      >
        <Tooltip
          title={t(isLight ? "nav.theme.dark" : "nav.theme.light")}
          placement="right"
          arrow
        >
          <IconButton
            onClick={onToggleColorMode}
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              color: "text.secondary",
              transition: "all 0.15s ease-in-out",
              "&:hover": {
                bgcolor: "action.hover",
                color: "text.primary",
              },
            }}
          >
            {isLight ? (
              <DarkModeOutlinedIcon sx={{ fontSize: 19 }} />
            ) : (
              <LightModeOutlinedIcon sx={{ fontSize: 19 }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title={t("nav.settings")} placement="right" arrow>
          <IconButton
            onClick={() => onSelectTab("settings")}
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              bgcolor:
                currentTab === "settings" ? "action.selected" : "transparent",
              color: currentTab === "settings" ? "#FF3567" : "text.secondary",
              transition: "all 0.15s ease-in-out",
              "&:hover": {
                bgcolor: "action.hover",
                color: "text.primary",
              },
            }}
          >
            <SettingsRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
